import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const officialLinks = [
  { href: "https://www.dorneylake.co.uk/", label: "Dorney Lake official website" },
  { href: "https://www.dorneylake.co.uk/events", label: "Dorney Lake events calendar" },
  { href: "https://w3w.co/entire.vibes.sport", label: "What3words: ///entire.vibes.sport" },
];

const dorneyLakeTaxiRoute = {
  id: "dorney-lake-taxi",
  path: "/dorney-lake-taxi",
  liveStripVariant: "windsor",
  seo: {
    title: "Dorney Lake Taxi - Event Transfers, Heathrow, Taplow & Windsor | CheapCabGrab",
    description:
      "Book Dorney Lake taxis for triathlons, regattas, running events, open-water swimming events, Taplow station transfers, Heathrow Airport and Windsor hotels. Fixed fares, licensed drivers, MPVs and minibuses.",
    keywords:
      "Dorney Lake taxi, Dorney Lake event taxi, Dorney Lake triathlon taxi, Dorney Lake to Heathrow taxi, Heathrow to Dorney Lake taxi, Taplow station to Dorney Lake taxi, Windsor to Dorney Lake taxi, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/dorney-lake-taxi",
    ogTitle: "Dorney Lake Taxi - Event Transfers, Heathrow, Taplow & Windsor",
    ogDescription:
      "Fixed-price taxis for Dorney Lake events, triathlons, regattas, running events, Taplow station connections, Heathrow Airport and nearby hotels.",
    serviceName: "CheapCabGrab - Dorney Lake Taxi Service",
    areaServed: [
      "Dorney Lake",
      "Dorney SL4 6FJ",
      "Taplow Station",
      "Windsor",
      "Eton",
      "Maidenhead",
      "Slough",
      "Heathrow Airport",
    ],
    offerCatalogName: "Dorney Lake Taxi Routes",
    offerName: "Dorney Lake fixed-price taxi service",
    offerDescription:
      "Fixed-price taxi transfers for Dorney Lake sports events, Taplow station, Windsor hotels, Maidenhead, Slough and Heathrow Airport.",
    reservationName: "Taxi booking for Dorney Lake",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "TaxiService",
      name: "CheapCabGrab - Dorney Lake Taxi Service",
      url: "https://www.cheapcabgrab.com/dorney-lake-taxi",
      serviceType: "Event taxi and airport transfer",
      provider: { "@type": "Organization", name: "CheapCabGrab" },
      areaServed: [
        "Dorney Lake",
        "Court Lane, Dorney, Windsor SL4 6FJ",
        "Taplow Station",
        "Windsor",
        "Maidenhead",
        "Slough",
        "Heathrow Airport",
      ],
      destination: {
        "@type": "Place",
        name: "Dorney Lake",
        address: "Dorney Lake, Court Lane, off Lake End Road, Dorney, Windsor, Berkshire, SL4 6FJ",
      },
      potentialAction: {
        "@type": "ReserveAction",
        target: "https://www.cheapcabgrab.com/dorney-lake-taxi",
        result: { "@type": "Reservation", name: "Dorney Lake taxi booking" },
      },
    },
  },
  hero: {
    eyebrow: "Dorney Lake event taxi transfers",
    h1: "Dorney Lake Taxi - Events, Races, Heathrow & Taplow Transfers",
    subtitle: "Fixed-price taxis for Dorney Lake, Taplow station, Windsor hotels and Heathrow Airport.",
    body:
      "Dorney Lake hosts regattas, triathlons, running events, charity sporting events and open-water swimming events. Book a licensed taxi, MPV or minibus for early starts, finish-line pickups, Heathrow transfers and Taplow Elizabeth Line connections.",
  },
  quote: {
    label: "Get instant Dorney Lake taxi quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "",
    presetDropoff: "Dorney Lake, Court Lane, Dorney, Windsor SL4 6FJ",
    pickupPlaceholder: "Enter pickup postcode, hotel, station or airport",
    dropoffPlaceholder: "Dorney Lake, Taplow station, Heathrow or home",
  },
  flags: [
    "Dorney Lake sports event, race day and regatta transfers",
    "Taplow station to Dorney Lake taxis for Elizabeth Line passengers",
    "Heathrow Airport is around 13 miles from Dorney Lake",
    "Saloon, MPV, minibus and coach options for teams and spectators",
  ],
  reviewPageKey: "/dorney-lake-taxi",
  reviewAriaLabel: "Dorney Lake taxi customer reviews",
  statusProfile: "windsor",
  lhrStatusKicker: "Live Dorney + Heathrow intelligence",
  lhrStatusTitle: "Live checks for Dorney Lake taxi transfers",
  floatingCta: "Book Dorney Lake Taxi",
  mobileCta: "Book Dorney Taxi",
  supportNudge: "Need Dorney Lake taxi help?",
  sections: [
    {
      id: "dorney-overview",
      title: "Dorney Lake taxi planning at a glance",
      body:
        "Dorney Lake is on Court Lane, off Lake End Road, Dorney, Windsor, Berkshire, SL4 6FJ. The main entrance is via Court Lane; the venue cannot be accessed directly from the Thames Path.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Venue details",
          items: [
            "Address: Dorney Lake, Court Lane, off Lake End Road, Dorney, Windsor SL4 6FJ",
            "What3words: ///entire.vibes.sport",
            "Main entrance: Court Lane, Dorney",
            "Near Windsor, Eton, Taplow, Maidenhead and Slough",
          ],
        },
        {
          title: "Useful journey basics",
          items: [
            "Heathrow Airport is around 13 miles away",
            "Taplow station is commonly used for Elizabeth Line connections",
            "Taplow to Dorney Lake is usually a short 5-10 minute taxi ride",
            "Road access is commonly via M4 Junction 7 and local Dorney roads",
          ],
        },
        {
          title: "Best for event days",
          items: [
            "Early race starts and athlete drop-offs",
            "Spectator pickups after triathlons, runs and regattas",
            "MPVs for bikes, kit bags and luggage",
            "Minibuses for clubs, teams and charity groups",
          ],
        },
      ]),
    },
    {
      id: "dorney-events",
      title: "Dorney Lake event taxi planning",
      body:
        "Dorney Lake event days can mean early arrivals, temporary event arrangements and heavier demand around Court Lane, Taplow station and local hotels. Pre-booking both outward and return journeys is recommended.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Sports event types",
          items: [
            "Regattas and rowing events",
            "Triathlon, duathlon and aquabike events",
            "Running events, 5k, 10k and half marathon days",
            "Open-water swimming, charity and corporate sporting events",
          ],
        },
        {
          title: "Known 2026 event patterns",
          items: [
            "June: triathlon/run days, One Race, Marlow Regatta and private regatta/event dates",
            "July: RunThrough events, Arctic One, Full Steam and family triathlon dates",
            "August: RunThrough events plus superhero and sidekick triathlon dates",
            "Always check your event organiser for final timings, access and parking rules",
          ],
        },
        {
          title: "Race day taxi notes",
          items: [
            "Confirm whether your event uses normal venue access or event-specific arrangements",
            "Allow extra time for road controls, car park queues and athlete kit handling",
            "Use phone contact details that work on event day",
            "For return trips, choose a realistic pickup time after medal collection, bag pickup and exit queues",
          ],
        },
      ]),
    },
    {
      id: "dorney-routes",
      title: "Popular Dorney Lake taxi routes",
      body:
        "Use this page for Dorney Lake itself, or connect via our local Windsor, Maidenhead, Slough and Heathrow taxi pages.",
      render: () => cardGrid("route-links-grid", [
        {
          title: "Taplow station to Dorney Lake",
          body:
            "A practical option for Elizabeth Line and Paddington passengers. Taplow is usually the closest simple rail connection, then a short taxi to Court Lane.",
          link: { to: "/taplow-taxi", label: "Open Taplow taxi", className: "route-link-btn" },
        },
        {
          title: "Heathrow to Dorney Lake",
          body:
            "Useful for international athletes, race staff and spectators flying into Heathrow before events at Dorney Lake.",
          links: [
            { to: "/heathrow-taxi", label: "Heathrow taxi" },
            { to: "/heathrow-to-windsor", label: "Heathrow to Windsor", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
        {
          title: "Windsor, Maidenhead and Slough",
          body:
            "Good for hotels, restaurants, team stays and local return journeys after Dorney Lake events.",
          links: [
            { to: "/windsor-taxi", label: "Windsor" },
            { to: "/maidenhead-taxi", label: "Maidenhead", className: "route-link-btn route-link-btn-secondary" },
            { to: "/slough-taxi", label: "Slough", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "dorney-vehicles",
      title: "Vehicles for Dorney Lake events",
      body:
        "Choose the vehicle around passengers, luggage, bikes, event kit and timing. Larger vehicles should be booked early for race weekends.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Saloon and estate cars",
          items: [
            "Solo spectators, officials and light luggage",
            "Taplow station to Dorney Lake connections",
            "Hotel to venue transfers around Windsor, Maidenhead and Slough",
          ],
        },
        {
          title: "MPV and executive MPV",
          items: [
            "Families, athletes and extra kit bags",
            "Airport transfers with luggage",
            "Better for event days than squeezing race bags into a small car",
          ],
        },
        {
          title: "Minibus and coach",
          items: [
            "Running clubs, rowing groups, triathlon teams and corporate groups",
            "Coordinated hotel, airport or station pickups",
            "Coach services are pre-booked and operated with licensed PSV partners",
          ],
          links: [
            { to: "/minibus", label: "Minibus" },
            { to: "/coach", label: "Coach", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "dorney-official-links",
      title: "Official Dorney Lake links",
      body:
        "Use Dorney Lake and your event organiser for final event timings, access, parking, barrier code and water-temperature information. CheapCabGrab handles taxi planning and booking.",
      render: () => (
        <div className="route-links-grid">
          {officialLinks.map((link) => (
            <article key={link.href}>
              <h3>{link.label}</h3>
              <p>Open the official venue or location information before confirming travel plans.</p>
              <a href={link.href} className="route-link-btn" target="_blank" rel="noreferrer">
                Open official page
              </a>
            </article>
          ))}
        </div>
      ),
    },
    {
      id: "dorney-faq",
      title: "Dorney Lake taxi - FAQs",
      body:
        "Taxi-focused answers for Dorney Lake sports events, hospitality bookings, station transfers and Heathrow airport connections.",
      render: () => (
        <div className="route-faq-grid">
          {[
            [
              "How do I get to Dorney Lake by taxi?",
              "Use Dorney Lake, Court Lane, off Lake End Road, Dorney, Windsor SL4 6FJ as the destination. The main entrance is via Court Lane; do not plan taxi access from the Thames Path.",
            ],
            [
              "Can I get a taxi from Taplow station to Dorney Lake?",
              "Yes. Taplow station is a useful rail connection on the Elizabeth Line, and the onward taxi ride to Dorney Lake is usually short. Pre-booking is recommended on event days.",
            ],
            [
              "Can you collect from Dorney Lake after a triathlon, run or regatta?",
              "Yes, but event-specific access and pickup arrangements can vary. Check your organiser's instructions, allow time to leave the event area and keep your phone available.",
            ],
            [
              "Is Dorney Lake close to Heathrow Airport?",
              "Yes. Dorney Lake is around 13 miles from Heathrow, making it practical for athletes, staff and spectators flying in or out around event weekends.",
            ],
            [
              "Do you offer MPVs or minibuses for teams?",
              "Yes. MPVs, minibuses and coach options are available for clubs, teams, spectators and corporate groups. Book larger vehicles early for busy race weekends.",
            ],
            [
              "Can taxis enter if there is a barrier code?",
              "If an event uses a barrier code or special access instruction, your event coordinator or organiser should provide it. Share access details with support before pickup where possible.",
            ],
            [
              "Can I leave a vehicle overnight at Dorney Lake?",
              "Dorney Lake says overnight parking cannot be guaranteed. If you need a late return or next-day pickup, pre-book taxi transport instead of relying on overnight parking.",
            ],
            [
              "Who confirms event parking, water temperature or race access?",
              "The event organiser controls event-specific arrangements such as parking, race access and water-temperature decisions. CheapCabGrab only handles taxi transport.",
            ],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
    {
      id: "dorney-support",
      title: "Need help with a Dorney Lake taxi booking?",
      body:
        "For event-day pickups, team transport, early starts, Heathrow arrivals or Taplow station connections, contact support before booking if you need help choosing a vehicle or pickup time.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            For related local journeys, see <Link to="/taplow-taxi">Taplow taxis</Link>,{" "}
            <Link to="/windsor-taxi">Windsor taxis</Link>,{" "}
            <Link to="/maidenhead-taxi">Maidenhead taxis</Link>,{" "}
            <Link to="/slough-taxi">Slough taxis</Link> and{" "}
            <Link to="/heathrow-taxi">Heathrow taxis</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const DorneyLakeTaxi = () => (
  <CcgRoutePremiumPage route={dorneyLakeTaxiRoute} QuoteComponent={PostcodeQuotenew} />
);

export default DorneyLakeTaxi;
